import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import {
  RadarChart,
  PieChart,
  GaugeChart,
  LineChart,
  BarChart,
  CustomChart,
} from 'echarts/charts';
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
  TitleComponent,
  GraphicComponent,
  MarkLineComponent,
  MarkPointComponent,
  DataZoomSliderComponent,
} from 'echarts/components';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { ChartOption, ChartEventHandlers } from '@app-types/chart';
import { CHART_TEXT_STYLE } from './chartConstants';

// Register only the modules the widget charts actually need. Anything not
// listed here will not be available, which keeps the bundle small.
echarts.use([
  RadarChart,
  PieChart,
  GaugeChart,
  LineChart,
  BarChart,
  CustomChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  TitleComponent,
  GraphicComponent,
  MarkLineComponent,
  MarkPointComponent,
  DataZoomSliderComponent,
  LabelLayout,
  CanvasRenderer,
]);

export interface BaseEChartProps {
  option: ChartOption;
  /** Pixel height of the chart canvas. */
  height: number;
  className?: string;
  /** Optional ECharts event handlers (e.g. { click: fn }). */
  onEvents?: ChartEventHandlers;
  /** Accessible summary of the chart purpose (see T13). */
  ariaLabel?: string;
  /** Enable animation. Off by default for compact widget charts. */
  animate?: boolean;
}

/**
 * Shared ECharts wrapper. Every chart in the app renders through this
 * component; pages and chart components must not import echarts-for-react
 * directly (see T04 acceptance criteria).
 */
const BaseEChart: React.FC<BaseEChartProps> = ({
  option,
  height,
  className,
  onEvents,
  ariaLabel,
  animate = false,
}) => {
  const chartRef = useRef<ReactEChartsCore>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Resize the chart when its container changes size (not just the window).
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let frameId: number | null = null;
    const requestFrame =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame
        : (callback: FrameRequestCallback) => window.setTimeout(callback, 0);
    const cancelFrame =
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame
        : window.clearTimeout;
    const scheduleResize = () => {
      if (frameId !== null) {
        return;
      }

      frameId = requestFrame(() => {
        frameId = null;
        chartRef.current?.getEchartsInstance().resize();
      });
    };

    const observer = new ResizeObserver(scheduleResize);
    observer.observe(node);
    return () => {
      if (frameId !== null) {
        cancelFrame(frameId);
      }
      observer.disconnect();
    };
  }, []);

  const mergedOption: ChartOption = {
    animation: animate,
    textStyle: CHART_TEXT_STYLE,
    ...option,
  };

  return (
    <div
      ref={containerRef}
      className={className}
      role="img"
      aria-label={ariaLabel}
      style={{ width: '100%', height }}
    >
      <ReactEChartsCore
        ref={chartRef}
        echarts={echarts}
        option={mergedOption}
        notMerge
        lazyUpdate
        style={{ width: '100%', height: '100%' }}
        onEvents={onEvents}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
};

export default BaseEChart;
